import React from "react";

import { TextureType } from "../materials/TextureLoader";

import TextureUpload from "./TextureUpload";

import ColorControl from "./ColorControl";

export default function SurfaceControls({
  surface,
  setSurface,
  Slider,
  textureId,
  setTextureId,
  textureOptions,
}) {
  function update(property, value) {
    setSurface((current) => ({
      ...current,
      [property]: value,
    }));
  }

  function updateMap(property, texture) {
    setSurface((current) => {
      const previous = current[property];

      if (previous && previous !== texture) {
        previous.dispose();
      }

      return {
        ...current,
        [property]: texture,
      };
    });
  }

  function updateScale(axis, value) {
    setSurface((current) => {
      const scale = [...current.textureScale];

      scale[axis] = value;

      return {
        ...current,
        textureScale: scale,
      };
    });
  }

  return (
    <>
      <ColorControl
        label="Base Color"
        value={surface.baseColor}
        onChange={(value) => update("baseColor", value)}
      />

      <div
        style={{
          marginBottom: "10px",
        }}
      >
        <span
          style={{
            display: "block",
            marginBottom: "6px",
            fontFamily: "monospace",
            fontSize: "10px",
            color: "#858d87",
          }}
        >
          Base Texture
        </span>

        <select
          value={textureId}
          onChange={(event) => setTextureId(event.target.value)}
          style={{
            width: "100%",
            padding: "7px 8px",
            marginBottom: "6px",
            border: "1px solid #303631",
            borderRadius: "3px",
            background: "#181b19",
            color: "#d5dbd6",
            fontFamily: "monospace",
            fontSize: "10px",
          }}
        >
          {textureOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
      </div>

      <TextureUpload
        label="Custom Base"
        texture={surface.baseTexture}
        type={TextureType.COLOR}
        onChange={(texture) => updateMap("baseTexture", texture)}
      />

      <TextureUpload
        label="Normal Map"
        texture={surface.normalMap}
        type={TextureType.DATA}
        onChange={(texture) => updateMap("normalMap", texture)}
      />

      {surface.normalMap && (
        <Slider
          label="Normal Strength"
          value={surface.normalStrength}
          min={0}
          max={3}
          step={0.01}
          display={surface.normalStrength.toFixed(2)}
          onChange={(value) => update("normalStrength", value)}
        />
      )}

      <TextureUpload
        label="Height Map"
        texture={surface.heightMap}
        type={TextureType.DATA}
        onChange={(texture) => updateMap("heightMap", texture)}
      />

      {surface.heightMap && (
        <Slider
          label="Bump Strength"
          value={surface.heightStrength}
          min={0}
          max={0.25}
          step={0.001}
          display={surface.heightStrength.toFixed(3)}
          onChange={(value) => update("heightStrength", value)}
        />
      )}

      <TextureUpload
        label="AO Map"
        texture={surface.aoMap}
        type={TextureType.DATA}
        onChange={(texture) => updateMap("aoMap", texture)}
      />

      <ColorControl
        label="Emissive"
        value={surface.emissiveColor}
        onChange={(value) => update("emissiveColor", value)}
      />

      <TextureUpload
        label="Emissive Map"
        texture={surface.emissiveMap}
        type={TextureType.COLOR}
        onChange={(texture) => updateMap("emissiveMap", texture)}
      />

      <Slider
        label="Texture Scale X"
        value={surface.textureScale[0]}
        min={0.25}
        max={8}
        step={0.05}
        display={surface.textureScale[0].toFixed(2)}
        onChange={(value) => updateScale(0, value)}
      />

      <Slider
        label="Texture Scale Y"
        value={surface.textureScale[1]}
        min={0.25}
        max={8}
        step={0.05}
        display={surface.textureScale[1].toFixed(2)}
        onChange={(value) => updateScale(1, value)}
      />

      <Slider
        label="Texture Rotation"
        value={surface.textureRotation}
        min={-Math.PI}
        max={Math.PI}
        step={0.01}
        display={((surface.textureRotation * 180) / Math.PI).toFixed(0) + "°"}
        onChange={(value) => update("textureRotation", value)}
      />
    </>
  );
}
